/**
 * One template in the library, with a real rendering of the letter on it.
 *
 * The preview is the actual mail HTML, scaled down inside a clipped box. Not a
 * screenshot and not a hand-drawn approximation: it is built by the same
 * renderer the send path uses, so a card cannot show a letter that no longer
 * exists. Rendering it costs a string concatenation, which is cheaper than
 * storing and invalidating thumbnails would be.
 *
 * `transform: scale` rather than an iframe. An iframe per card would be a
 * document load per card, and the letter's own styles are all inline, so there
 * is nothing here for the CRM's stylesheet to fight with.
 */
import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { cn } from "@wa/ui";
import { useT } from "../../../../i18n/I18nProvider";
import { parseDesign, renderEmailHtml, type EmailDesign } from "../../../../emailBuilder";
import { CountBadge, dt } from "../shared";

const PREVIEW_H = 150;

/**
 * The box's real width, watched.
 *
 * The scale has to come from a measurement, not a constant: these cards sit in
 * a grid that is two columns at one breakpoint and four at another, so any
 * guessed width is wrong at every size but one - and being wrong here means the
 * letter is silently cropped down its right edge, which is exactly the thing a
 * preview exists to rule out.
 */
function useBoxWidth() {
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new ResizeObserver(([entry]) => setWidth(entry?.contentRect.width ?? 0));
    observer.observe(el);
    setWidth(el.getBoundingClientRect().width);
    return () => observer.disconnect();
  }, []);
  return [ref, width] as const;
}

export function TemplateCard({
  to,
  name,
  description,
  subject,
  locale,
  usedBy,
  updatedAt,
  design,
  tag,
  className,
  actions,
}: {
  to?: string;
  name: string;
  description?: string;
  subject?: string;
  locale?: string;
  usedBy?: number;
  updatedAt?: string;
  /** Either handed in directly (a preset) or read from the row's stored JSON. */
  design?: EmailDesign | unknown | null;
  /** A short state word next to the badges, e.g. "edited". */
  tag?: string;
  className?: string;
  actions?: ReactNode;
}) {
  const t = useT();
  const [boxRef, boxWidth] = useBoxWidth();
  const parsed = design && typeof design === "object" ? parseDesign(design) : null;
  const width = parsed?.page.contentWidth ?? 600;
  const scale = boxWidth > 0 ? boxWidth / width : 0;

  const body = (
    <div
      ref={boxRef}
      className="relative overflow-hidden rounded-lg border border-border bg-muted/30"
      style={{ height: PREVIEW_H }}
    >
      {parsed ? (
        // Hidden until measured. One frame at the wrong scale is a visible
        // jump on every card in the grid.
        <div
          aria-hidden
          className="pointer-events-none absolute left-0 top-0 origin-top-left"
          style={{ width, transform: `scale(${scale})`, visibility: scale ? "visible" : "hidden" }}
          dangerouslySetInnerHTML={{ __html: renderEmailHtml(parsed) }}
        />
      ) : (
        // A template written straight in HTML has no design to draw. Saying so
        // beats an empty box that reads as a failed load.
        <p className="flex h-full items-center justify-center px-4 text-center text-[11px] text-muted-foreground">
          {t("marketing.templates.noPreview")}
        </p>
      )}
    </div>
  );

  return (
    <article
      className={cn(
        "flex flex-col gap-3 rounded-2xl border border-border bg-card/70 p-3 shadow-soft backdrop-blur transition-colors",
        to && "hover:border-primary/40",
        className,
      )}
    >
      {to ? (
        <Link to={to} className="block">
          {body}
        </Link>
      ) : (
        body
      )}

      <div className="min-w-0 flex-1 space-y-1">
        <h3 className="truncate text-sm font-medium">
          {to ? (
            <Link to={to} className="hover:underline">
              {name}
            </Link>
          ) : (
            name
          )}
        </h3>
        {subject && <p className="truncate text-[11px] text-muted-foreground">{subject}</p>}
        {description && <p className="line-clamp-2 text-[11px] leading-snug text-muted-foreground/80">{description}</p>}
      </div>

      <div className="flex flex-wrap items-center gap-1.5 text-[10px] text-muted-foreground">
        {locale && <CountBadge>{t(`marketing.locale.${locale}`)}</CountBadge>}
        {tag && <CountBadge>{tag}</CountBadge>}
        {usedBy != null && usedBy > 0 && (
          <CountBadge>{t("marketing.templates.usedBy", { n: usedBy })}</CountBadge>
        )}
        {updatedAt && <span className="ml-auto">{dt(updatedAt)}</span>}
      </div>

      {actions && <div className="flex flex-wrap items-center justify-end gap-1">{actions}</div>}
    </article>
  );
}
